/**
 * DailyQuoteCard — F-016 (2026-05-22).
 *
 * Full-width Hero-Dashboard card that surfaces one quote per day,
 * deterministically rotated by date over the `DAILY_QUOTES` pool (3 original
 * seeds + 100 public-domain classics, EN + DE).
 *
 * Why not random? A client opening the dashboard twice in a day should
 * see the same quote — repeats build resonance, randomness feels like
 * noise.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DAILY_QUOTES } from '@/lib/daily-quotes';

const QUOTES = DAILY_QUOTES;

function epochDayIndex(): number {
  // Day index since unix epoch — stable across timezones at UTC midnight,
  // good enough to rotate one quote per calendar day.
  const ms = Date.now();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

export function DailyQuoteCard(): JSX.Element {
  const { t, i18n } = useTranslation();
  const quote = useMemo(() => {
    const idx = epochDayIndex() % QUOTES.length;
    return QUOTES[idx]!;
  }, []);
  const lang = i18n.language === 'de' ? 'de' : 'en';
  return (
    <article
      className="bg-bone-2 rounded-lg p-s6 border border-bone-3 shadow-sm"
      data-testid="card-daily-quote"
    >
      <p className="font-mono text-xs uppercase tracking-wide text-flame-ember mb-s2">
        {t('dashboard.dailyQuote.eyebrow')}
      </p>
      <blockquote className="font-display text-display-sm text-ink italic leading-relaxed border-l-4 border-flame pl-s4">
        &ldquo;{quote.text[lang]}&rdquo;
      </blockquote>
      <p className="mt-s3 text-sm text-gray-2 text-right">
        — {quote.author}
      </p>
    </article>
  );
}
